import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLenis } from "lenis/react";
import { Menu, X, Check, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { NAV_LINKS, DISCORD_URL } from "../../lib/site";
import { LANGS } from "../../i18n/translations";
import { useI18n } from "../../i18n/LanguageContext";
import { DiscordIcon } from "./DiscordIcon";

export const Header = () => {
  const { t, lang, setLang } = useI18n();
  const lenis = useLenis();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const activeLang = LANGS.find((l) => l.value === lang) ?? LANGS[0];

  const scrollTo = (href) => {
    if (lenis) lenis.scrollTo(href, { offset: -80 });
    else document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };
  const toTop = () => (lenis ? lenis.scrollTo(0) : window.scrollTo({ top: 0, behavior: "smooth" }));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="header-nav"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "glass border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-[72px] flex items-center justify-between">
        <button data-testid="logo-home" onClick={toTop} className="flex items-center gap-3 group">
          <span className="h-10 w-10 rounded-xl bg-brand grid place-items-center font-display font-extrabold text-[#080a0f] text-lg shadow-[0_0_20px_rgba(56,189,248,0.4)] transition-transform duration-300 group-hover:rotate-6">
            M
          </span>
          <span className="font-display font-bold text-lg tracking-tight">MShop</span>
        </button>

        <nav className="hidden md:flex items-center gap-9 absolute left-1/2 -translate-x-1/2">
          {NAV_LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-${l.href.replace("#", "")}`}
              onClick={() => scrollTo(l.href)}
              className="text-sm text-slate-300 hover:text-white transition-colors duration-200 relative after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-cyan-400 after:transition-all after:duration-300 hover:after:w-full"
            >
              {t.nav[l.key]}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="lang-trigger"
                className="flex items-center gap-2 rounded-full border border-white/10 px-3.5 py-2 text-sm text-slate-200 hover:border-white/20 transition-colors"
              >
                <span className="font-mono text-[10px] text-cyan-400">{activeLang.code}</span>
                <span className="hidden sm:inline">{activeLang.label}</span>
                <ChevronDown className="h-3.5 w-3.5 text-slate-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#0a0e14] border-white/10 text-slate-200">
              {LANGS.map((l) => (
                <DropdownMenuItem
                  key={l.code}
                  data-testid={`lang-${l.value}`}
                  onSelect={() => setLang(l.value)}
                  className="gap-2 focus:bg-white/5 focus:text-white cursor-pointer"
                >
                  <span className="font-mono text-[10px] text-cyan-400 w-5">{l.code}</span>
                  {l.label}
                  {lang === l.value && <Check className="h-3.5 w-3.5 ml-auto text-cyan-400" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <a
            data-testid="header-discord-btn"
            href={DISCORD_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-brand text-[#080a0f] font-semibold text-sm px-4 py-2.5 rounded-full flex items-center gap-2 hover:shadow-[0_0_24px_rgba(56,189,248,0.5)] transition-shadow duration-300 active:scale-[0.97]"
          >
            <DiscordIcon className="h-4 w-4" />
            {t.common.discord}
          </a>

          <button
            data-testid="mobile-menu-btn"
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden h-10 w-10 grid place-items-center rounded-xl border border-white/10 text-slate-200"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden glass border-t border-white/5 overflow-hidden"
          >
            <div className="px-5 py-5 flex flex-col gap-4">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`mobile-nav-${l.href.replace("#", "")}`}
                  onClick={() => { scrollTo(l.href); setMobileOpen(false); }}
                  className="text-left text-slate-200 py-1"
                >
                  {t.nav[l.key]}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
